import React, { useEffect, useState } from 'react';
import { getAds, saveAd, deleteAd, toggleAdStatus, uploadFileToServer } from '../services/api';
import { PlusCircle, Trash, ExternalLink, Edit2 } from 'lucide-react';

const ManageAds = () => {
  const [ads, setAds] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({ title: '', link: '', image: '', image_alt: '', image_title: '', placement: 'sidebar', status: 'active', page_name: 'global' });
  const [isUploading, setIsUploading] = useState(false);

  const loadAds = async () => {
    const data = await getAds();
    setAds(data);
  };

  useEffect(() => {
    loadAds();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    await saveAd(formData);
    setFormData({ title: '', link: '', image: '', image_alt: '', image_title: '', placement: 'sidebar', status: 'active', page_name: 'global' });
    setIsCreating(false);
    setIsUploading(false);
    loadAds();
    loadAds();
  };

  const handleToggleStatus = async (ad) => {
    await toggleAdStatus(ad);
    loadAds();
  };

  const handleEdit = (ad) => {
    setFormData(ad);
    setIsCreating(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if(window.confirm('Delete this ad?')) {
      await deleteAd(id);
      loadAds();
    }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-8 pt-2">
        <div>
          <h1 className="text-3xl font-extrabold text-stone-900 mb-2">Advertisement Campaigns</h1>
          <p className="text-stone-500">Configure global ad banners across frontend placements.</p>
        </div>
        <button 
          onClick={() => {
            if (isCreating && formData.id) {
              // Closing the edit form
              setFormData({ title: '', link: '', image: '', image_alt: '', image_title: '', placement: 'sidebar', status: 'active', page_name: 'global' });
            } else if (!isCreating) {
              // Opening new form
              setFormData({ title: '', link: '', image: '', image_alt: '', image_title: '', placement: 'sidebar', status: 'active', page_name: 'global' });
            }
            setIsCreating(!isCreating);
          }}
          className="inline-flex items-center px-4 py-2.5 bg-saffron-600 text-white font-medium rounded-xl hover:bg-saffron-700 transition"
        >
          <PlusCircle className="w-5 h-5 mr-2" /> New Ad
        </button>
      </div>

      {isCreating && (
        <div className="bg-stone-50 rounded-2xl border border-stone-200 p-6 mb-8 border-l-4 border-l-saffron-500">
          <h2 className="text-lg font-bold text-stone-800 mb-4">{formData.id ? 'Update Advertisement' : 'Create Advertisement'}</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Company / Title</label>
              <input required value={formData.title} onChange={e=>setFormData({...formData, title: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5" placeholder="Spritual Books Pvt." />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Target Link (Optional)</label>
              <input type="url" value={formData.link || ''} onChange={e=>setFormData({...formData, link: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5" placeholder="https://..." />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Banner Image URL or Upload</label>
              <div className="flex flex-col space-y-2">
                <input type="url" value={formData.image.startsWith('data:') ? '' : formData.image} onChange={e=>setFormData({...formData, image: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5" placeholder="Paste URL..." required={!formData.image} />
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setIsUploading(true);
                      const res = await uploadFileToServer(file);
                      if (res.success) {
                        setFormData({...formData, image: res.url});
                      } else {
                        alert("Failed to upload image: " + res.message);
                      }
                      setIsUploading(false);
                    }
                  }}
                  disabled={isUploading}
                  className="w-full text-sm text-stone-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-stone-100 file:text-stone-700 hover:file:bg-stone-200 cursor-pointer disabled:opacity-50"
                />
                {isUploading && <span className="text-xs text-saffron-600 font-bold animate-pulse">Uploading image...</span>}
              </div>
              <div className="flex space-x-2 mt-3">
                <input value={formData.image_alt || ''} onChange={e=>setFormData({...formData, image_alt: e.target.value})} className="flex-1 w-full border border-stone-300 rounded-lg p-2.5 text-sm bg-white placeholder-stone-400 focus:outline-none focus:ring-1 focus:ring-saffron-500" placeholder="Alt Text (SEO descriptive tag)" />
                <input value={formData.image_title || ''} onChange={e=>setFormData({...formData, image_title: e.target.value})} className="flex-1 w-full border border-stone-300 rounded-lg p-2.5 text-sm bg-white placeholder-stone-400 focus:outline-none focus:ring-1 focus:ring-saffron-500" placeholder="Title Text (Optional Hover)" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Placement</label>
                <select value={formData.placement} onChange={e=>setFormData({...formData, placement: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 bg-white">
                  <option value="sidebar">Right Sidebar</option>
                  <option value="banner">Top Banner</option>
                  <option value="in-content">Article Body</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Target Page</label>
                <select value={formData.page_name} onChange={e=>setFormData({...formData, page_name: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 bg-white">
                  <option value="global">Global (All Pages)</option>
                  <option value="home">Home</option>
                  <option value="about">About</option>
                  <option value="temples">Temples</option>
                  <option value="temple_detail">Temple Detail</option>
                  <option value="blog">Blog</option>
                  <option value="forum">Forum</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Status</label>
                <select value={formData.status} onChange={e=>setFormData({...formData, status: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 bg-white">
                  <option value="active">Active</option>
                  <option value="inactive">Paused</option>
                </select>
              </div>
            </div>
            <div className="md:col-span-2 flex justify-end space-x-3 mt-4">
              <button type="button" onClick={() => { setIsCreating(false); setFormData({ title: '', link: '', image: '', image_alt: '', image_title: '', placement: 'sidebar', status: 'active', page_name: 'global' }); }} className="px-6 py-2.5 text-stone-600 hover:bg-stone-200 bg-stone-100 rounded-xl font-medium transition">Cancel</button>
              <button type="submit" disabled={isUploading} className="px-6 py-2.5 text-white bg-saffron-600 hover:bg-saffron-700 rounded-xl font-medium transition disabled:bg-stone-400">
                {isUploading ? 'Please Wait...' : (formData.id ? 'Update Campaign' : 'Save Campaign')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {ads.map(ad => (
          <div key={ad.id} className="border border-stone-200 rounded-2xl overflow-hidden bg-white shadow-sm flex flex-col group">
             <div className="h-40 bg-stone-100 overflow-hidden relative border-b border-stone-100">
                <img src={ad.image} alt={ad.title} className="w-full h-full object-cover" />
                <button 
                  onClick={() => handleToggleStatus(ad)}
                  className={`absolute top-2 right-2 px-2.5 py-1 text-[10px] font-bold uppercase rounded-full tracking-wider transition hover:opacity-80 ${ad.status === 'active' ? 'bg-green-500 text-white' : 'bg-stone-500 text-white'}`}
                  title="Toggle ad status"
                >
                  {ad.status}
                </button>
             </div>
             <div className="p-5 flex-1 flex flex-col">
               <div className="flex justify-between items-center mb-2">
                 <span className="text-xs font-bold text-saffron-600 uppercase tracking-widest">{ad.placement} placement</span>
                 <span className="text-[10px] font-semibold text-stone-500 bg-stone-100 border border-stone-200 px-2 py-0.5 rounded-md uppercase tracking-wide">{ad.page_name || 'global'}</span>
               </div>
               <h3 className="font-extrabold text-stone-900 text-lg mb-4">{ad.title}</h3>
               
               <div className="mt-auto flex justify-between items-center border-t border-stone-100 pt-4">
                 <a href={ad.link || '#'} target={ad.link ? '_blank' : '_self'} rel="noreferrer" className={`text-sm font-semibold flex items-center ${ad.link ? 'text-stone-500 hover:text-blue-600' : 'text-stone-300 cursor-default'}`}>
                   <ExternalLink className="w-4 h-4 mr-1"/> {ad.link ? 'Test Link' : 'No Link'}
                 </a>
                 <div className="flex space-x-1">
                   <button onClick={() => handleEdit(ad)} className="text-blue-400 hover:text-blue-600 p-2 rounded-lg hover:bg-blue-50 transition">
                     <Edit2 className="w-5 h-5"/>
                   </button>
                   <button onClick={() => handleDelete(ad.id)} className="text-red-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition">
                     <Trash className="w-5 h-5"/>
                   </button>
                 </div>
               </div>
             </div>
          </div>
        ))}
      </div>
      
    </div>
  );
};

export default ManageAds;
